import SEO from "@/components/SEO";
import PageHero from "@/components/sections/PageHero";
import { brand } from "@/content/site";

const owner = brand.owner;
const abn = brand.abn;

const sections = [
  {
    h: "1. Introduction",
    p: [
      `${owner} (ABN ${abn}) ("we", "us", "our") is committed to protecting your privacy and handling your personal information in accordance with Australian privacy laws, including the Privacy Act 1988 (Cth) and the Australian Privacy Principles.`,
      "This Privacy Policy explains how we collect, use, disclose and protect your personal information when you use our website and services.",
    ],
  },
  {
    h: "2. Information we collect",
    p: ["We may collect the following types of personal information:"],
    listTitle: "Information you provide directly:",
    list: [
      "Name (first name and last name)",
      "Business name",
      "Email address",
      "Mobile or business phone number",
      "Primary trade or business type",
      "Primary suburb or service area",
      "Website address and approximate team size",
      "Information about your growth goals and current challenges",
    ],
    listTitle2: "Information collected automatically:",
    list2: [
      "IP address",
      "Browser type and version",
      "Device information",
      "Pages visited and time spent on our website",
      "Referring website and campaign parameters (UTM values)",
      "Cookies and similar technologies",
    ],
  },
  {
    h: "3. How we use your information",
    p: ["We use your personal information to:"],
    list: [
      "Respond to your enquiry and arrange a Growth Assessment",
      "Provide the services you request",
      "Communicate with you about our services",
      "Improve our website and services",
      "Comply with legal obligations",
      "Prevent fraud and ensure security",
    ],
  },
  {
    h: "4. Sharing your information",
    p: ["We may share your personal information with:"],
    list: [
      "Service providers who help us operate our website and business",
      "Professional advisers (e.g. lawyers, accountants)",
      "Government authorities when required by law",
    ],
    after: ["We do not sell your personal information to third parties for marketing purposes."],
  },
  {
    h: "5. Marketing and communications",
    p: [
      "Any marketing communication we send is subject to your consent and applicable communication requirements, and you can unsubscribe at any time.",
      "Where undertaken on behalf of a client, customer-database reactivation activity is subject to consent, privacy, identification and unsubscribe requirements.",
    ],
  },
  {
    h: "6. Data security",
    p: [
      "We take reasonable steps to protect your personal information from misuse, interference, loss, and unauthorised access, modification or disclosure. Our measures include secure (HTTPS) connections, restricted access to personal information and regular security assessments.",
    ],
  },
  {
    h: "7. Cookies and tracking",
    p: [
      "We use cookies and similar technologies to enhance your experience, understand how you use our website and improve our services. You can manage your cookie preferences through your browser settings. Disabling cookies may affect the functionality of our website.",
    ],
  },
  {
    h: "8. Your rights",
    p: ["Under Australian privacy law, you have the right to:"],
    list: [
      "Access the personal information we hold about you",
      "Request correction of inaccurate information",
      "Request deletion of your personal information (subject to legal requirements)",
      "Opt out of marketing communications",
      "Make a complaint about our handling of your information",
    ],
  },
  {
    h: "9. Data retention",
    p: [
      "We retain your personal information for as long as necessary to fulfil the purposes for which it was collected, or as required by law. When your information is no longer needed, we will securely delete or de-identify it.",
    ],
  },
  {
    h: "10. Third party websites",
    p: [
      "Our website may contain links to third-party websites. We are not responsible for the privacy practices of these websites. We encourage you to read the privacy policies of any websites you visit.",
    ],
  },
  {
    h: "11. Changes to this policy",
    p: [
      'We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. We encourage you to review this policy periodically.',
    ],
  },
  {
    h: "12. Contact us",
    p: [
      `If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact us at:`,
      `${owner}`,
      `ABN: ${abn}`,
      `Email: ${brand.email}`,
      `Website: ${brand.name}`,
    ],
  },
  {
    h: "13. Complaints",
    p: [
      "If you are not satisfied with our response to a privacy concern, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC): www.oaic.gov.au, phone 1300 363 992.",
    ],
  },
];

function List({ title, items }) {
  if (!items) return null;
  return (
    <div className="mt-4">
      {title && <p className="font-bold text-forest">{title}</p>}
      <ul className="mt-2 space-y-2">
        {items.map((i) => (
          <li key={i} className="flex items-start gap-3 text-mid-neutral">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-mint shrink-0" />
            {i}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy | Green Circle Group"
        description="How Green Circle Group collects, uses and protects personal information submitted through Green Circle Group."
        path="/privacy"
      />
      <PageHero
        eyebrow="Legal"
        heading="Privacy Policy"
        copy="Last updated: December 2025"
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Privacy", to: "/privacy" },
        ]}
      />
      <section className="bg-white pb-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 space-y-10">
          {sections.map((s) => (
            <div key={s.h}>
              <h2 className="text-2xl font-extrabold text-forest">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="mt-3 text-mid-neutral leading-relaxed">
                  {para}
                </p>
              ))}
              <List title={s.listTitle} items={s.list} />
              <List title={s.listTitle2} items={s.list2} />
              {s.after?.map((para, i) => (
                <p key={i} className="mt-4 text-mid-neutral leading-relaxed">
                  {para}
                </p>
              ))}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
